import isNumber from 'lodash-es/isNumber';
import {
    COMMENTS_FETCH_FAILURE,
    COMMENTS_FETCH_REQUEST,
    COMMENTS_FETCH_SUCCESS,
    CommentsFetchFailureAction,
    CommentsFetchRequestAction,
    CommentsFetchSuccessAction,
} from 'mk2/apps/blogs/containers/Comments/Comments.actions';
import {
    GROUP_SHARED_DELETE_GROUP_POST_API_SUCCESS,
    GroupSharedDeleteGroupPostApiSuccessAction,
} from 'mk2/apps/groups/containers/GroupShared/GroupShared.actions';
import { HINTS_CLOSE_HINT, HintsCloseHintAction } from 'mk2/apps/users/containers/Hints/Hints.actions';
import { GROUP_SUBSCRIBED_FEED_HINT_NAME } from 'mk2/apps/users/containers/Hints/Hints.constants';
import { tupdate } from 'mk2/helpers/types';
import { errorMessage } from 'mk2/reducers/errorMessage';
import { loadingState, LoadingState } from 'mk2/reducers/loadingState';
import { Reducer } from 'redux';
import {
    GROUP_SUBSCRIBED_FEED_FETCH_FAILURE,
    GROUP_SUBSCRIBED_FEED_FETCH_REQUEST,
    GROUP_SUBSCRIBED_FEED_FETCH_SUCCESS,
    GroupSubscribedFeedFetchFailureAction,
    GroupSubscribedFeedFetchRequestAction,
    GroupSubscribedFeedFetchSuccessAction,
} from './GroupSubscribedFeed.actions';

export type OrderBy = 'comments' | 'posts';

export interface GroupSubscribedFeedPageState {
    postIds: number[];
    errorMessage: string;
    loadingState: LoadingState;
}

export const initialGroupSubscribedFeedPageState: GroupSubscribedFeedPageState = {
    postIds: [],
    errorMessage: null,
    loadingState: LoadingState.INIT,
};

export interface GroupSubscribedFeedVirtualPageState {
    errorMessage: string;
    loadingState: LoadingState;
}

export const initialGroupSubscribedFeedVirtualPageState: GroupSubscribedFeedVirtualPageState = {
    errorMessage: null,
    loadingState: LoadingState.INIT,
};

export interface PostsState {
    count: number;
    pages: {[pageNum: number]: GroupSubscribedFeedPageState};
    virtualPages: {[pageName: string]: GroupSubscribedFeedVirtualPageState};  // pageName = 'first'|'last'...
    numberOfPages: number;
}

export const initialPostsState: PostsState = {
    count: 0,
    pages: {},
    virtualPages: {},
    numberOfPages: null,
};

type PostsReducerAction =
    GroupSubscribedFeedFetchRequestAction
    | GroupSubscribedFeedFetchSuccessAction
    | GroupSubscribedFeedFetchFailureAction
    | GroupSharedDeleteGroupPostApiSuccessAction;

const postsReducer: Reducer<PostsState> = (state = initialPostsState, action: PostsReducerAction) => {
    switch (action.type) {
        case GROUP_SUBSCRIBED_FEED_FETCH_REQUEST:
        case GROUP_SUBSCRIBED_FEED_FETCH_FAILURE:
            if (isNumber(action.pageRef)) {
                return tupdate(state, {
                    pages: tupdate(state.pages, {
                        [action.pageRef]: tupdate(state.pages[action.pageRef] || initialGroupSubscribedFeedPageState, {
                            loadingState: loadingState(undefined, action),
                            errorMessage: errorMessage(undefined, action),
                        }),
                    }),
                });
            } else {
                return tupdate(state, {
                    virtualPages: tupdate(state.virtualPages, {
                        [action.pageRef]: tupdate(state.pages[action.pageRef] || initialGroupSubscribedFeedVirtualPageState, {
                            loadingState: loadingState(undefined, action),
                            errorMessage: errorMessage(undefined, action),
                        }),
                    }),
                });
            }

        case GROUP_SUBSCRIBED_FEED_FETCH_SUCCESS:
            if (!isNumber(action.page)) {
                throw new Error('Page must be a number');
            }

            return tupdate(state, {
                count: action.count,
                numberOfPages: action.numberOfPages,
                // update state of page under both - virtual name and real index
                virtualPages: isNumber(action.pageRef) ? state.virtualPages : tupdate(state.virtualPages, {
                    [action.pageRef]: tupdate(state.virtualPages[action.pageRef] || initialGroupSubscribedFeedVirtualPageState, {
                        loadingState: loadingState(undefined, action),
                        errorMessage: errorMessage(undefined, action),
                    }),
                }),
                pages: tupdate(state.pages, {
                    [action.page]: tupdate(state.pages[action.page] || initialGroupSubscribedFeedPageState, {
                        postIds: action.response.result.posts,
                        loadingState: loadingState(undefined, action),
                        errorMessage: errorMessage(undefined, action),
                    }),
                }),
            });

        default:
            return state;
    }
};

type GroupSubscribedFeedByOrderingState = {
    [orderBy in OrderBy]?: PostsState;
};

export interface GroupSubscribedFeedState {
    commentsLoadingState: LoadingState;
    errorMessage: string;
    moderators: number[];
    orders: GroupSubscribedFeedByOrderingState;
    showHint: boolean;
}

export const initialGroupSubscribedFeedState: GroupSubscribedFeedState = {
    commentsLoadingState: LoadingState.INIT,
    errorMessage: null,
    moderators: [],
    orders: {},
    showHint: false,
};

declare type GroupSubscribedFeedReducerAction =
    CommentsFetchRequestAction
    | CommentsFetchSuccessAction
    | CommentsFetchFailureAction
    | GroupSubscribedFeedFetchRequestAction
    | GroupSubscribedFeedFetchSuccessAction
    | GroupSubscribedFeedFetchFailureAction
    | GroupSharedDeleteGroupPostApiSuccessAction
    | HintsCloseHintAction;

const groupSubscribedFeedReducer: Reducer<GroupSubscribedFeedState> = (state = initialGroupSubscribedFeedState, action: GroupSubscribedFeedReducerAction) => {
    switch (action.type) {
        case COMMENTS_FETCH_REQUEST:
        case COMMENTS_FETCH_SUCCESS:
        case COMMENTS_FETCH_FAILURE:
            return tupdate(state, {
                commentsLoadingState: loadingState(undefined, action),
            });

        case GROUP_SUBSCRIBED_FEED_FETCH_REQUEST:
        case GROUP_SUBSCRIBED_FEED_FETCH_FAILURE:
            return tupdate(state, {
                orders: tupdate(state.orders, {
                    [action.orderBy]: postsReducer(state.orders[action.orderBy], action),
                }),
            });

        case GROUP_SUBSCRIBED_FEED_FETCH_SUCCESS:
            return tupdate(state, {
                orders: tupdate(state.orders, {
                    [action.orderBy]: postsReducer(state.orders[action.orderBy], action),
                }),
                moderators: action.response.result.moderators,
                showHint: action.showHint,
            });

        case GROUP_SHARED_DELETE_GROUP_POST_API_SUCCESS:
            return tupdate(state, {
                orders: Object.keys(state.orders).reduce((acc, key) => {
                    acc[key] = postsReducer(state.orders[key], action);
                    return acc;
                }, {}),
            });

        case HINTS_CLOSE_HINT:
            return (action.name === GROUP_SUBSCRIBED_FEED_HINT_NAME)
                ? tupdate(state, {
                    showHint: false,
                })
                : state;

        default:
            return state;
    }
};

export default groupSubscribedFeedReducer;
